import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { get, post, patch } from "../services/apiClient";

export type InvoiceType = "BUSINESS_REGISTRATION" | "SUBSCRIPTION";
export type InvoiceStatus =
  | "PENDING"
  | "PAID"
  | "FAILED"
  | "CANCELLED"
  | "REFUNDED";

export interface InvoiceRow {
  id: string;
  invoiceNumber: string;
  type: InvoiceType;
  status: InvoiceStatus;
  ownerId: string;
  businessId?: string | null;
  subscriptionId?: string | null;
  subtotal: number | string;
  taxAmount: number | string;
  discountAmount: number | string;
  totalAmount: number | string;
  currency: string;
  description?: string | null;
  notes?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  issueDate: string;
  dueDate?: string | null;
  paidAt?: string | null;
  paymentMethod?: string | null;
  paymentReference?: string | null;
  owner?: {
    id: string;
    name: string;
    phone: string;
    email?: string | null;
  } | null;
  business?: {
    id: string;
    businessName: string;
    nameEn: string;
    nameMl: string;
  } | null;
  subscription?: {
    id: string;
    status: string;
    billingCycle: string;
    unitAmount: number | string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    plan?: { id: string; name: string; code: string } | null;
  } | null;
}

export interface InvoiceListParams {
  page?: number;
  limit?: number;
  type?: InvoiceType;
  status?: InvoiceStatus;
  ownerId?: string;
  businessId?: string;
  subscriptionId?: string;
  search?: string;
}

interface InvoiceListResponse {
  data: InvoiceRow[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const useInvoicesList = (
  params: InvoiceListParams = {},
  options?: Omit<UseQueryOptions<InvoiceListResponse>, "queryKey" | "queryFn">,
) =>
  useQuery<InvoiceListResponse>({
    queryKey: ["invoices", "list", params],
    queryFn: () => get<InvoiceListResponse>("/admin/invoices", params),
    retry: false,
    ...options,
  });

export const useMarkInvoicePaid = () => {
  const qc = useQueryClient();
  return useMutation<
    InvoiceRow,
    Error,
    { id: string; paymentMethod?: string; paymentReference?: string }
  >({
    mutationFn: ({ id, paymentMethod, paymentReference }) =>
      post(`/admin/invoices/${id}/pay`, { paymentMethod, paymentReference }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
      qc.invalidateQueries({ queryKey: ["subscriptions"] });
    },
  });
};

export const useCancelInvoice = () => {
  const qc = useQueryClient();
  return useMutation<InvoiceRow, Error, { id: string; reason?: string }>({
    mutationFn: ({ id, reason }) =>
      post(`/admin/invoices/${id}/cancel`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
};

export const useUpdateRegistrationFee = () => {
  const qc = useQueryClient();
  return useMutation<
    unknown,
    Error,
    { businessId: string; registrationFee: number }
  >({
    mutationFn: ({ businessId, registrationFee }) =>
      patch(`/admin/invoices/business/${businessId}/registration-fee`, {
        registrationFee,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["businesses"] });
      qc.invalidateQueries({ queryKey: ["invoices"] });
    },
  });
};
